/* global GPUBufferUsage, GPUTextureUsage, GPUShaderStage, GPUMapMode, BigUint64Array */
// WebGPU backend of the physical paint engine. All simulation state lives in
// persistent GPU buffers; every physical step (water, paper, pigment, paint
// body, drying), brush/knife material exchange, pigment shading and lighting
// run as WGSL on the GPU. The CPU only runs input, bristle kinematics
// (O(bristle groups) per step), scheduling and command encoding. Nothing is
// read back during painting; readback() exists for validation and saving.
//
// The engine exposes the same stroke/step API as core/PaintEngine.js so the
// deterministic scenarios and the CPU-vs-GPU validation run on both.

import { PIGMENT_SLOTS, TILE_SIZE, scalarFieldNames } from '../core/PaintState.js';
import { createScheduler, stepsForFrame, SIM_STEP } from '../core/SimulationScheduler.js';
import { createSurface } from '../surfaces/PaperModel.js';
import { resolveMedium } from '../materials/MediumModel.js';
import { resolvePigment } from '../pigments/PigmentLibrary.js';
import { BAND_COUNT } from '../pigments/ColourConversion.js';
import { brushPreset } from '../brushes/BrushPresets.js';
import { createBristleBrush, beginBristleStroke, continueBristleStroke } from '../brushes/BristleBrush.js';
import { createPaletteKnife, beginKnifeStroke, continueKnifeStroke } from '../brushes/PaletteKnife.js';
import { STAMP_STRIDE, writeStampRecord } from '../brushes/StampExchange.js';
import { DEFAULT_LIGHT, lightColour, lightVector } from '../rendering/Lighting.js';
import { buildSimShader, maxWaterSubsteps, SCRATCH, GLOBAL, PIGMENT_RECORD } from './simShader.js';
import { buildBrushShader, brushKernelTiers, CARRIER_STRIDE, KNIFE_STEP_STRIDE, MAX_FOOTPRINT, MAX_GROUPS } from './brushShader.js';
import { buildShadeShader, buildLightShader, LIGHT_UNIFORM_BYTES } from './renderShader.js';
import { paperSpectrum } from '../pigments/PigmentLibrary.js';
import { spectrumToLinearRgb, hexToSrgb, srgbToLinear } from '../pigments/ColourConversion.js';
import { saundersonBody } from '../pigments/SpectralMixing.js';
import { MAX_BODY_SUBSTEPS } from '../physics/ViscousPaint.js';

// Kernels that run only on region tiles (indirect dispatch).
const REGION_KERNELS = new Set(['reduceMaxima', 'transportFaces', 'transportScale', 'transportRatios', 'transportApply', 'diffuseSnapshot', 'diffuseApply', 'paperExposure', 'paperLocal', 'paperWick', 'pigmentExchange', 'bodyDrying', 'activityCells']);
const SCALARS = scalarFieldNames();
const MAX_STAMPS_PER_FLUSH = 8192;
const SUB_STRIDE = 256; // dynamic uniform offset alignment
const VIEWS = { shaded: 0, pigment: 1, height: 2, wetness: 3, normals: 4 };

export function isWebGPUAvailable() {
  return typeof navigator !== 'undefined' && Boolean(navigator.gpu);
}

// Requests a device with storage limits large enough for big canvases.
export async function requestPaintDevice() {
  if (!isWebGPUAvailable()) return null;
  const adapter = await navigator.gpu.requestAdapter({ powerPreference: 'high-performance' });
  if (!adapter) return null;
  const features = [];
  if (adapter.features.has('timestamp-query')) features.push('timestamp-query');
  // Subgroup operations let the brush kernel use a large workgroup.
  if (adapter.features.has('subgroups')) features.push('subgroups');
  const device = await adapter.requestDevice({
    requiredFeatures: features,
    requiredLimits: {
      maxStorageBufferBindingSize: adapter.limits.maxStorageBufferBindingSize,
      maxBufferSize: adapter.limits.maxBufferSize,
      // The brush kernel keeps all bristle carriers in workgroup memory.
      maxComputeWorkgroupStorageSize: adapter.limits.maxComputeWorkgroupStorageSize,
      maxComputeInvocationsPerWorkgroup: adapter.limits.maxComputeInvocationsPerWorkgroup,
      maxComputeWorkgroupSizeX: adapter.limits.maxComputeWorkgroupSizeX,
    },
  });
  brushCaps.set(device, {
    subgroups: features.includes('subgroups'),
    minSubgroupSize: (adapter.info && adapter.info.subgroupMinSize) || 4,
    maxInvocations: Math.min(device.limits.maxComputeInvocationsPerWorkgroup, device.limits.maxComputeWorkgroupSizeX),
  });
  let info = null;
  try {
    info = adapter.info || (adapter.requestAdapterInfo ? await adapter.requestAdapterInfo() : null);
  } catch (error) {
    info = null;
  }
  return { adapter, device, info, timestamps: features.includes('timestamp-query') };
}

// Shader modules, layouts and pipelines are shared by every engine on a
// device (one per paint layer), so additional layers cost buffers only.
const pipelineCache = new WeakMap();
// Brush kernel capabilities per device (set when the device is requested).
const brushCaps = new WeakMap();
export function brushKernelInfo(device) {
  return brushKernelTiers(brushCaps.get(device) || {});
}

function getPipelines(device) {
  let cached = pipelineCache.get(device);
  if (cached) return cached;
  const storageEntry = (binding, readOnly = false) => ({ binding, visibility: GPUShaderStage.COMPUTE, buffer: { type: readOnly ? 'read-only-storage' : 'storage' } });
  const uniformEntry = (binding, dynamic = false) => ({ binding, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform', hasDynamicOffset: dynamic } });
  const simModule = device.createShaderModule({ label: 'paint.sim', code: buildSimShader() });
  const brushTiers = brushKernelInfo(device).map((tier) => ({
    ...tier,
    module: device.createShaderModule({ label: `paint.brush${tier.config.threads}`, code: buildBrushShader(tier.config) }),
  }));
  const brushModule = brushTiers[0].module;
  const shadeModule = device.createShaderModule({ label: 'paint.shade', code: buildShadeShader() });
  const lightModule = device.createShaderModule({ label: 'paint.light', code: buildLightShader() });
  const simLayout0 = device.createBindGroupLayout({ entries: [uniformEntry(0), storageEntry(1), storageEntry(2), storageEntry(3), storageEntry(4, true), storageEntry(5), storageEntry(6), uniformEntry(7)] });
  const indirectLayout = device.createBindGroupLayout({ entries: [storageEntry(0)] });
  const simLayout1 = device.createBindGroupLayout({ entries: [uniformEntry(0, true)] });
  const simPipelineLayout = device.createPipelineLayout({ bindGroupLayouts: [simLayout0, simLayout1] });
  const indirectPipelineLayout = device.createPipelineLayout({ bindGroupLayouts: [simLayout0, simLayout1, indirectLayout] });
  const sim = {};
  for (const entryPoint of ['resetGlobals', 'writeIndirect', 'buildRegion', 'reduceMaxima', 'planSubsteps', 'transportFaces', 'transportScale', 'transportRatios', 'transportApply', 'diffuseSnapshot', 'diffuseApply', 'paperExposure', 'paperLocal', 'paperWick', 'pigmentExchange', 'bodyDrying', 'activityCells', 'activityTiles', 'dryAll']) {
    const layout = entryPoint === 'writeIndirect' ? indirectPipelineLayout : simPipelineLayout;
    sim[entryPoint] = device.createComputePipeline({ label: `paint.${entryPoint}`, layout, compute: { module: simModule, entryPoint } });
  }
  const brushLayout = device.createBindGroupLayout({ entries: [uniformEntry(0), storageEntry(1), storageEntry(2), storageEntry(3, true), storageEntry(4), uniformEntry(5), storageEntry(6), storageEntry(7, true), storageEntry(8), storageEntry(9, true)] });
  const brushPipelineLayout = device.createPipelineLayout({ bindGroupLayouts: [brushLayout] });
  const shadeLayout = device.createBindGroupLayout({
    entries: [
      uniformEntry(0), storageEntry(1, true), storageEntry(2, true), storageEntry(3, true), storageEntry(4, true), storageEntry(5), uniformEntry(6),
      { binding: 7, visibility: GPUShaderStage.COMPUTE, storageTexture: { access: 'write-only', format: 'rgba8unorm' } },
      { binding: 8, visibility: GPUShaderStage.COMPUTE, storageTexture: { access: 'write-only', format: 'r32float' } },
      { binding: 9, visibility: GPUShaderStage.COMPUTE, storageTexture: { access: 'write-only', format: 'rgba8unorm' } },
    ],
  });
  const shadePipelineLayout = device.createPipelineLayout({ bindGroupLayouts: [shadeLayout] });
  const lightLayout = device.createBindGroupLayout({
    entries: [
      { binding: 0, visibility: GPUShaderStage.FRAGMENT, buffer: { type: 'uniform' } },
      { binding: 1, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
      { binding: 2, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'unfilterable-float' } },
      { binding: 3, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'float' } },
      { binding: 4, visibility: GPUShaderStage.FRAGMENT, sampler: { type: 'filtering' } },
      { binding: 5, visibility: GPUShaderStage.FRAGMENT, texture: { sampleType: 'unfilterable-float' } },
    ],
  });
  const lightPipelines = new Map();
  cached = {
    sim,
    simLayout0,
    simLayout1,
    indirectLayout,
    brushLayout,
    // Bristle-stamp kernels by footprint size (same maths, different workgroup size).
    brushPipes: brushTiers.map((tier) => ({
      maxCells: tier.maxCells,
      threads: tier.config.threads,
      pipe: device.createComputePipeline({ label: `paint.brushStamps${tier.config.threads}`, layout: brushPipelineLayout, compute: { module: tier.module, entryPoint: 'brushStamps' } }),
    })),
    knifePipe: device.createComputePipeline({ label: 'paint.knifeSteps', layout: brushPipelineLayout, compute: { module: brushModule, entryPoint: 'knifeSteps' } }),
    shadeLayout,
    shadePipe: device.createComputePipeline({ layout: shadePipelineLayout, compute: { module: shadeModule, entryPoint: 'shadeTiles' } }),
    clearDirtyPipe: device.createComputePipeline({ layout: shadePipelineLayout, compute: { module: shadeModule, entryPoint: 'clearDirty' } }),
    lightLayout,
    sampler: device.createSampler({ magFilter: 'linear', minFilter: 'linear' }),
    lightPipeline(format) {
      if (!lightPipelines.has(format)) {
        lightPipelines.set(format, device.createRenderPipeline({
          layout: device.createPipelineLayout({ bindGroupLayouts: [lightLayout] }),
          vertex: { module: lightModule, entryPoint: 'vs' },
          fragment: { module: lightModule, entryPoint: 'fs', targets: [{ format }] },
          primitive: { topology: 'triangle-list' },
        }));
      }
      return lightPipelines.get(format);
    },
  };
  pipelineCache.set(device, cached);
  return cached;
}

// Linear-RGB albedo of a reflectance spectrum (Saunderson body reflection).
function spectrumAlbedo(spectrum) {
  return spectrumToLinearRgb(Array.from(spectrum, (r) => saundersonBody(r)));
}

// Neutral white substrate (option substrate: 'white').
export const WHITE_SUBSTRATE = paperSpectrum(0.97, 0);

// Replaces the substrate spectrum in cells that hold exactly the old one.
const SUBSTRATE_UNIFORM_BYTES = 112;
const substratePipelines = new WeakMap();
function getSubstratePipeline(device) {
  let pipeline = substratePipelines.get(device);
  if (pipeline) return pipeline;
  const module = device.createShaderModule({
    label: 'paint.substrate',
    code: `
struct Sub { old: array<vec4<f32>, 3>, next: array<vec4<f32>, 3>, n: u32, };
@group(0) @binding(0) var<storage, read_write> R: array<f32>;
@group(0) @binding(1) var<uniform> sub: Sub;
@compute @workgroup_size(64)
fn replaceSubstrate(@builtin(global_invocation_id) gid: vec3<u32>) {
  let i = gid.x;
  if (i >= sub.n) { return; }
  for (var b = 0u; b < ${BAND_COUNT}u; b++) {
    let o = sub.old[b / 4u];
    if (abs(R[i * ${BAND_COUNT}u + b] - o[b % 4u]) > 1e-5) { return; }
  }
  for (var b = 0u; b < ${BAND_COUNT}u; b++) {
    let v = sub.next[b / 4u];
    R[i * ${BAND_COUNT}u + b] = v[b % 4u];
  }
}`,
  });
  pipeline = device.createComputePipeline({ label: 'paint.replaceSubstrate', layout: 'auto', compute: { module, entryPoint: 'replaceSubstrate' } });
  substratePipelines.set(device, pipeline);
  return pipeline;
}

// Compiles every pipeline ahead of the first stroke (shader compilation is
// the slowest part of a cold start).
export function warmPaintPipelines(device, format = 'rgba8unorm') {
  const pipes = getPipelines(device);
  pipes.lightPipeline(format);
  return pipes;
}

export function createGpuPaintEngine(gpu, options = {}) {
  const { device } = gpu;
  const width = Math.max(8, options.width | 0 || 512);
  const height = Math.max(8, options.height | 0 || 384);
  const surface = options.surfaceData || createSurface(width, height, options.surface || 'cold_press', options.seed ?? 7, options.surfaceOverrides);
  const substrateSpectrum = options.substrate === 'white'
    ? WHITE_SUBSTRATE
    : options.substrate && options.substrate.length === BAND_COUNT ? Float32Array.from(options.substrate) : null;
  const initialSpectrum = substrateSpectrum || paperSpectrum(surface.preset.tone ?? 0.86, surface.preset.warmth ?? 0.04);
  const n = width * height;
  const tilesX = Math.ceil(width / TILE_SIZE);
  const tilesY = Math.ceil(height / TILE_SIZE);
  const nt = tilesX * tilesY;
  const buffers = [];
  let allocated = 0;

  const makeBuffer = (label, bytes, usage, data = null) => {
    const size = Math.max(16, Math.ceil(bytes / 4) * 4);
    const buffer = device.createBuffer({ label, size, usage, mappedAtCreation: Boolean(data) });
    if (data) {
      new (data.constructor)(buffer.getMappedRange()).set(data);
      buffer.unmap();
    }
    buffers.push(buffer);
    allocated += size;
    return buffer;
  };
  const STORAGE = GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC | GPUBufferUsage.COPY_DST;
  const UNIFORM = GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST;

  const S = makeBuffer('paint.S', SCALARS.length * n * 4, STORAGE);
  const P = makeBuffer('paint.P', 3 * PIGMENT_SLOTS * n * 4, STORAGE);
  // Dried stack starts as the substrate spectrum, written straight into the
  // mapped GPU buffer (no full CPU paint state is allocated).
  const R = (() => {
    const buffer = device.createBuffer({ label: 'paint.R', size: n * BAND_COUNT * 4, usage: STORAGE, mappedAtCreation: true });
    const data = new Float32Array(buffer.getMappedRange());
    data.set(initialSpectrum, 0);
    for (let filled = BAND_COUNT; filled < data.length; filled *= 2) data.copyWithin(filled, 0, Math.min(filled, data.length - filled));
    buffer.unmap();
    buffers.push(buffer);
    allocated += n * BAND_COUNT * 4;
    return buffer;
  })();
  const surfData = new Float32Array(4 * n);
  surfData.set(surface.heightField, 0);
  surfData.set(surface.absorbency, n);
  surfData.set(surface.capillary, 2 * n);
  surfData.set(surface.pinning, 3 * n);
  const SURF = makeBuffer('paint.surface', surfData.byteLength, STORAGE, surfData);
  const X = makeBuffer('paint.scratch', SCRATCH.count * n * 4, STORAGE);
  const tileInit = new Uint32Array(5 * nt + GLOBAL.count);
  tileInit.fill(1, 2 * nt, 3 * nt); // everything dirty once
  const T = makeBuffer('paint.tiles', tileInit.byteLength, STORAGE, tileInit);
  const PARAMS = makeBuffer('paint.params', 48, UNIFORM);
  const INDIRECT = makeBuffer('paint.indirect', 16, GPUBufferUsage.STORAGE | GPUBufferUsage.INDIRECT | GPUBufferUsage.COPY_SRC);
  const PIG = makeBuffer('paint.pigments', PIGMENT_SLOTS * PIGMENT_RECORD * 4, UNIFORM);
  const maxW = maxWaterSubsteps(SIM_STEP);
  const subCount = maxW + MAX_BODY_SUBSTEPS + 1;
  const subData = new Uint32Array((subCount * SUB_STRIDE) / 4);
  for (let s = 0; s < maxW; s += 1) subData.set([s, 0], (s * SUB_STRIDE) / 4);
  for (let s = 0; s < MAX_BODY_SUBSTEPS; s += 1) subData.set([s, 1], ((maxW + s) * SUB_STRIDE) / 4);
  subData.set([0, 2], ((maxW + MAX_BODY_SUBSTEPS) * SUB_STRIDE) / 4);
  const SUB = makeBuffer('paint.substeps', subData.byteLength, UNIFORM, subData);
  const CARRIERS = makeBuffer('paint.carriers', 2 * MAX_GROUPS * CARRIER_STRIDE * 4, STORAGE);
  const STAMPS = makeBuffer('paint.stamps', MAX_STAMPS_PER_FLUSH * STAMP_STRIDE * 4, GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST);
  const STROKE = makeBuffer('paint.stroke', 80, UNIFORM);
  const FOOT = makeBuffer('paint.footprint', MAX_FOOTPRINT * 3 * 4, GPUBufferUsage.STORAGE);

  const textureUsage = GPUTextureUsage.STORAGE_BINDING | GPUTextureUsage.TEXTURE_BINDING;
  const albedoTex = device.createTexture({ size: [width, height], format: 'rgba8unorm', usage: textureUsage });
  const elevationTex = device.createTexture({ size: [width, height], format: 'r32float', usage: textureUsage });
  const materialTex = device.createTexture({ size: [width, height], format: 'rgba8unorm', usage: textureUsage });
  allocated += n * 12;

  // ---------------------------------------------------------------- pipelines (shared per device)
  const pipes = getPipelines(device);
  const { sim, knifePipe, shadePipe, clearDirtyPipe } = pipes;
  const simGroup0 = device.createBindGroup({ layout: pipes.simLayout0, entries: [PARAMS, S, P, R, SURF, X, T, PIG].map((buffer, binding) => ({ binding, resource: { buffer } })) });
  const indirectGroup = device.createBindGroup({ layout: pipes.indirectLayout, entries: [{ binding: 0, resource: { buffer: INDIRECT } }] });
  const simGroup1 = device.createBindGroup({ layout: pipes.simLayout1, entries: [{ binding: 0, resource: { buffer: SUB, size: 16 } }] });
  // Cell -> UV island (u32, 0 = no surface) for 3D surface painting; a dummy otherwise.
  const ISLANDS = options.cellIslands && options.cellIslands.length === n
    ? makeBuffer('paint.islands', n * 4, GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST, Uint32Array.from(options.cellIslands))
    : makeBuffer('paint.islands', 16, GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST);
  const brushGroup = device.createBindGroup({ layout: pipes.brushLayout, entries: [PARAMS, S, P, SURF, T, STROKE, CARRIERS, STAMPS, FOOT, ISLANDS].map((buffer, binding) => ({ binding, resource: { buffer } })) });
  const shadeGroup = device.createBindGroup({
    layout: pipes.shadeLayout,
    entries: [
      ...[PARAMS, S, P, R, SURF, T, PIG].map((buffer, binding) => ({ binding, resource: { buffer } })),
      { binding: 7, resource: albedoTex.createView() },
      { binding: 8, resource: elevationTex.createView() },
      { binding: 9, resource: materialTex.createView() },
    ],
  });
  // Visual paper relief (0..1), sampled by the light pass.
  const paperTex = device.createTexture({ size: [width, height], format: 'r32float', usage: GPUTextureUsage.TEXTURE_BINDING | GPUTextureUsage.COPY_DST });
  allocated += n * 4;
  const LIGHT = makeBuffer('paint.light', LIGHT_UNIFORM_BYTES, UNIFORM);
  const lightGroup = device.createBindGroup({
    layout: pipes.lightLayout,
    entries: [
      { binding: 0, resource: { buffer: LIGHT } },
      { binding: 1, resource: albedoTex.createView() },
      { binding: 2, resource: elevationTex.createView() },
      { binding: 3, resource: materialTex.createView() },
      { binding: 4, resource: pipes.sampler },
      { binding: 5, resource: paperTex.createView() },
    ],
  });
  const display = {
    reliefStrength: 1,
    paperAlbedo: spectrumAlbedo(substrateSpectrum && options.substrate !== 'white' ? substrateSpectrum : paperSpectrum(surface.preset.tone ?? 0.87, surface.preset.warmth ?? 0.04)),
    substrateAlbedo: spectrumAlbedo(substrateSpectrum || paperSpectrum(surface.preset.tone ?? 0.87, surface.preset.warmth ?? 0.04)),
  };

  // ---------------------------------------------------------------- timing
  const timing = { enabled: gpu.timestamps, lastStepGpuMs: null, lastBrushGpuMs: null, pending: false, frames: 0 };
  let querySet = null;
  let queryResolve = null;
  let queryRead = null;
  if (timing.enabled) {
    querySet = device.createQuerySet({ type: 'timestamp', count: 4 });
    queryResolve = device.createBuffer({ size: 32, usage: GPUBufferUsage.QUERY_RESOLVE | GPUBufferUsage.COPY_SRC });
    queryRead = device.createBuffer({ size: 32, usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST });
  }

  // ---------------------------------------------------------------- state on the CPU side
  const slots = new Array(PIGMENT_SLOTS).fill(null);
  const engine = {
    kind: 'webgpu',
    version: 1,
    width,
    height,
    surface,
    env: { tiltX: 0, tiltY: 0, humidity: 0.4, dryingScale: 1, evaporationScale: 1, ...(options.env || {}) },
    scheduler: createScheduler({ maxStepsPerFrame: 8, maxBacklog: 0.5 }),
    tool: null,
    // stamps: bristle-group stamps sent to the GPU; footprintCells: their
    // bounding-box cells (before wet spread); brushDispatches: brush/knife
    // dispatches; steps: simulation steps.
    stats: { steps: 0, simSeconds: 0, stamps: 0, footprintCells: 0, brushDispatches: 0 },
    timing,
    memoryBytes: () => allocated,
    state: { width, height, time: 0, slots, surface },
  };

  const writeParams = (dt) => {
    const humidity = Math.max(0, Math.min(0.98, engine.env.humidity ?? 0.4));
    const evapScale = (engine.env.evaporationScale ?? 1) * (engine.surface.preset.drying ?? 1) * (1 - humidity) / 0.6;
    const data = new ArrayBuffer(48);
    const u = new Uint32Array(data);
    const fl = new Float32Array(data);
    u[0] = width; u[1] = height; u[2] = tilesX; u[3] = tilesY;
    fl[4] = dt; fl[5] = engine.env.tiltX || 0; fl[6] = engine.env.tiltY || 0; fl[7] = evapScale;
    fl[8] = engine.env.dryingScale ?? 1;
    let mask = 0;
    slots.forEach((p, k) => { if (p) mask |= 1 << k; });
    u[9] = mask;
    fl[10] = engine.surface.preset.sheen ?? 0.06;
    fl[11] = engine.surface.preset.granulation ?? 1;
    device.queue.writeBuffer(PARAMS, 0, data);
  };

  const writePigments = () => {
    const data = new Float32Array(PIGMENT_SLOTS * PIGMENT_RECORD);
    slots.forEach((p, k) => {
      if (!p) return;
      const o = k * PIGMENT_RECORD;
      data.set(p.K, o);
      data.set(p.S, o + 12);
      data[o + 24] = p.granulation;
      data[o + 25] = p.staining;
      data[o + 26] = p.density;
      data[o + 27] = 1;
    });
    device.queue.writeBuffer(PIG, 0, data);
  };

  // Slot allocation without reading GPU state: slots are never freed during a
  // session; beyond 8 pigments the spectrally nearest slot is reused.
  const slotFor = (pigment) => {
    const existing = slots.findIndex((p) => p && p.id === pigment.id);
    if (existing >= 0) return existing;
    const free = slots.findIndex((p) => !p);
    if (free >= 0) {
      slots[free] = pigment;
      writePigments();
      return free;
    }
    let best = 0;
    let bestD = Infinity;
    slots.forEach((other, k) => {
      let d = 0;
      for (let b = 0; b < BAND_COUNT; b += 1) d += (Math.log1p(other.K[b] / other.S[b]) - Math.log1p(pigment.K[b] / pigment.S[b])) ** 2;
      if (d < bestD) { bestD = d; best = k; }
    });
    return best;
  };

  const resolveMixture = (pigments) => {
    const list = (pigments || []).map((e) => (typeof e === 'string' ? { pigment: e, fraction: 1 } : e));
    const resolved = list
      .map((e) => ({ pigment: typeof e.pigment === 'object' ? e.pigment : resolvePigment(e.pigment), fraction: Math.max(0, Number(e.fraction) || 0) }))
      .filter((e) => e.pigment && e.fraction > 0);
    const total = resolved.reduce((s, e) => s + e.fraction, 0) || 1;
    return resolved.map((e) => ({ slot: slotFor(e.pigment), fraction: e.fraction / total }));
  };

  // ---------------------------------------------------------------- strokes
  const stampData = new Float32Array(MAX_STAMPS_PER_FLUSH * STAMP_STRIDE);
  let stampCount = 0;
  // Largest base footprint (cells) in the queued stamps: selects the kernel tier.
  let batchCells = 0;
  let strokeKind = 'brush';

  const writeStroke = (count) => {
    const tool = engine.tool;
    const data = new ArrayBuffer(80);
    const fl = new Float32Array(data);
    const u = new Uint32Array(data);
    const m = tool.medium;
    if (tool.kind === 'knife') {
      const pr = tool.preset;
      fl[0] = tool.impl.radius;
      fl[14] = pr.bladeLength || 2; fl[15] = pr.bladeAngle || 0; fl[16] = pr.flex ?? 0.3;
      fl[17] = pr.scrape ?? 0.8; fl[18] = pr.spread ?? 0.6; fl[19] = pr.edgeRidge ?? 0.3;
    } else {
      const ep = tool.impl.exchangeParams;
      fl[0] = ep.radius; fl[1] = ep.release; fl[2] = ep.stiffness; fl[3] = ep.plough;
      fl[4] = ep.pickupScale; fl[5] = ep.tooth;
    }
    u[6] = count;
    u[7] = tool.kind === 'knife' ? 1 : 0;
    fl[8] = m.rewet; fl[9] = m.mobility; fl[10] = m.yieldStress; fl[11] = m.bodyDrying; fl[12] = m.heightGain; fl[13] = m.wetGloss;
    device.queue.writeBuffer(STROKE, 0, data);
  };

  const flush = () => {
    if (!stampCount || !engine.tool) return;
    const stride = strokeKind === 'knife' ? KNIFE_STEP_STRIDE : STAMP_STRIDE;
    device.queue.writeBuffer(STAMPS, 0, stampData.buffer, 0, stampCount * stride * 4);
    writeStroke(stampCount);
    const encoder = device.createCommandEncoder();
    const pass = encoder.beginComputePass(timing.enabled && !timing.pending ? { timestampWrites: { querySet, beginningOfPassWriteIndex: 2, endOfPassWriteIndex: 3 } } : undefined);
    pass.setPipeline(strokeKind === 'knife' ? knifePipe : (pipes.brushPipes.find((t) => batchCells <= t.maxCells) || pipes.brushPipes[pipes.brushPipes.length - 1]).pipe);
    pass.setBindGroup(0, brushGroup);
    pass.dispatchWorkgroups(1);
    pass.end();
    device.queue.submit([encoder.finish()]);
    engine.stats.brushDispatches += 1;
    stampCount = 0;
    batchCells = 0;
  };
  // Sends queued stamps to the GPU now (tests and profiling).
  engine.flushStamps = () => flush();

  const pushStamp = (stamp) => {
    if (stampCount >= MAX_STAMPS_PER_FLUSH) flush();
    writeStampRecord(stampData, stampCount * STAMP_STRIDE, stamp);
    stampCount += 1;
    engine.stats.stamps += 1;
    const side = 2 * Math.ceil(stamp.baseRadius) + 1;
    engine.stats.footprintCells += side * side;
    batchCells = Math.max(batchCells, side * side);
  };
  const pushBlade = (step) => {
    if (stampCount >= MAX_STAMPS_PER_FLUSH) flush();
    stampData.set([step.cx, step.cy, step.pressure, step.ds, step.dirX, step.dirY, 0, 0], stampCount * KNIFE_STEP_STRIDE);
    stampCount += 1;
  };

  const uploadCarriers = (tool) => {
    const data = new Float32Array(2 * MAX_GROUPS * CARRIER_STRIDE);
    const put = (carrier, offset) => {
      data[offset] = carrier.water;
      data[offset + 1] = carrier.body;
      data[offset + 2] = carrier.capacity;
      data.set(carrier.pigW, offset + 4);
      data.set(carrier.pigB, offset + 12);
    };
    if (tool.kind === 'knife') put(tool.impl.carrier, 0);
    else {
      if (tool.impl.carriers.length > MAX_GROUPS) throw new Error(`Brush has more than ${MAX_GROUPS} bristle groups.`);
      tool.impl.carriers.forEach((c, g) => put(c, g * 2 * CARRIER_STRIDE));
      tool.impl.tipLoads.forEach((c, g) => put(c, (g * 2 + 1) * CARRIER_STRIDE));
    }
    device.queue.writeBuffer(CARRIERS, 0, data);
  };

  engine.beginStroke = (toolOptions, sample) => {
    flush();
    const preset = typeof toolOptions.brush === 'object' ? toolOptions.brush : brushPreset(toolOptions.brush);
    const medium = toolOptions.mediumParams || resolveMedium(toolOptions.medium ?? 0.2, toolOptions.mediumOverrides);
    const mixture = resolveMixture(toolOptions.pigments);
    writeParams(SIM_STEP);
    const common = { radius: toolOptions.radius ?? 10, medium, mixture, loading: toolOptions.loading ?? 1, seed: toolOptions.seed ?? 1, capacity: toolOptions.capacity ?? 1 };
    // The CPU tool object provides kinematics and initial loads; its sink
    // routes every per-cell operation to the GPU.
    const shadow = { width, height, surface };
    if (preset.tool === 'knife') {
      strokeKind = 'knife';
      const impl = createPaletteKnife(preset, common);
      // A surface adapter (3D) may map each blade step before it reaches the GPU.
      impl.sink = engine.bladeMapper ? (step) => engine.bladeMapper(step, pushBlade) : pushBlade;
      engine.tool = { kind: 'knife', preset, medium, impl };
      uploadCarriers(engine.tool);
      beginKnifeStroke(impl, shadow, sample);
    } else {
      strokeKind = 'brush';
      const impl = createBristleBrush(preset, { ...common, rotationMode: toolOptions.rotationMode, fixedAngle: toolOptions.fixedAngle, tooth: engine.surface.preset.friction ?? engine.surface.preset.tooth });
      // A surface adapter (3D) may map each stamp (to zero or more stamps on the grid).
      impl.sink = engine.stampMapper ? (stamp) => engine.stampMapper(stamp, pushStamp) : pushStamp;
      engine.tool = { kind: 'brush', preset, medium, impl };
      uploadCarriers(engine.tool);
      beginBristleStroke(impl, shadow, sample);
    }
    return engine.tool;
  };

  engine.strokeTo = (sample) => {
    if (!engine.tool) return;
    const shadow = { width, height, surface };
    if (engine.tool.kind === 'knife') continueKnifeStroke(engine.tool.impl, shadow, sample);
    else continueBristleStroke(engine.tool.impl, shadow, sample);
  };

  engine.endStroke = () => {
    flush();
    const tool = engine.tool;
    engine.tool = null;
    return tool;
  };

  // ---------------------------------------------------------------- simulation
  const dispatchGrid = (pass, pipeline, offset = 0) => {
    pass.setPipeline(pipeline);
    pass.setBindGroup(0, simGroup0);
    pass.setBindGroup(1, simGroup1, [offset]);
    if (REGION_KERNELS.has(pipeline.label.replace('paint.', ''))) pass.dispatchWorkgroupsIndirect(INDIRECT, 0);
    else pass.dispatchWorkgroups(Math.ceil(width / 8), Math.ceil(height / 8));
  };
  const dispatchOne = (pass, pipeline) => {
    pass.setPipeline(pipeline);
    pass.setBindGroup(0, simGroup0);
    pass.setBindGroup(1, simGroup1, [0]);
    if (pipeline === sim.writeIndirect) pass.setBindGroup(2, indirectGroup);
    pass.dispatchWorkgroups(1);
  };
  const dispatchTiles = (pass, pipeline) => {
    pass.setPipeline(pipeline);
    pass.setBindGroup(0, simGroup0);
    pass.setBindGroup(1, simGroup1, [0]);
    pass.dispatchWorkgroups(Math.ceil(nt / 64));
  };
  const transport = (pass, offset) => {
    dispatchGrid(pass, sim.transportFaces, offset);
    dispatchGrid(pass, sim.transportScale, offset);
    dispatchGrid(pass, sim.transportRatios, offset);
    dispatchGrid(pass, sim.transportApply, offset);
  };

  const encodeStep = (pass) => {
    dispatchOne(pass, sim.resetGlobals);
    dispatchTiles(pass, sim.buildRegion);
    dispatchOne(pass, sim.writeIndirect);
    dispatchGrid(pass, sim.reduceMaxima);
    dispatchOne(pass, sim.planSubsteps);
    for (let s = 0; s < maxW; s += 1) {
      const offset = s * SUB_STRIDE;
      transport(pass, offset);
      dispatchGrid(pass, sim.diffuseSnapshot, offset);
      dispatchGrid(pass, sim.diffuseApply, offset);
    }
    const rimOffset = (maxW + MAX_BODY_SUBSTEPS) * SUB_STRIDE;
    dispatchGrid(pass, sim.paperExposure, rimOffset);
    transport(pass, rimOffset);
    dispatchGrid(pass, sim.paperLocal, rimOffset);
    dispatchGrid(pass, sim.paperWick, rimOffset);
    dispatchGrid(pass, sim.pigmentExchange, rimOffset);
    for (let s = 0; s < MAX_BODY_SUBSTEPS; s += 1) transport(pass, (maxW + s) * SUB_STRIDE);
    dispatchGrid(pass, sim.bodyDrying);
    dispatchGrid(pass, sim.activityCells);
    dispatchTiles(pass, sim.activityTiles);
  };

  // Runs `count` fixed steps of dt in one submission.
  engine.runSteps = (count, dt = SIM_STEP) => {
    if (count <= 0) return;
    flush();
    writeParams(dt);
    const encoder = device.createCommandEncoder();
    const measure = timing.enabled && !timing.pending;
    const pass = encoder.beginComputePass(measure ? { timestampWrites: { querySet, beginningOfPassWriteIndex: 0, endOfPassWriteIndex: 1 } } : undefined);
    for (let s = 0; s < count; s += 1) encodeStep(pass);
    pass.end();
    if (measure) {
      encoder.resolveQuerySet(querySet, 0, 4, queryResolve, 0);
      encoder.copyBufferToBuffer(queryResolve, 0, queryRead, 0, 32);
    }
    device.queue.submit([encoder.finish()]);
    if (measure) {
      timing.pending = true;
      const steps = count;
      queryRead.mapAsync(GPUMapMode.READ).then(() => {
        const t = new BigUint64Array(queryRead.getMappedRange().slice(0));
        queryRead.unmap();
        if (t[1] > t[0]) timing.lastStepGpuMs = Number(t[1] - t[0]) / 1e6 / steps;
        if (t[3] > t[2]) timing.lastBrushGpuMs = Number(t[3] - t[2]) / 1e6;
        timing.pending = false;
      }).catch(() => { timing.pending = false; });
    }
    engine.stats.steps += count;
    engine.state.time += count * dt;
  };

  engine.step = (dt = SIM_STEP) => engine.runSteps(1, dt);
  engine.simulate = (seconds) => {
    let steps = Math.round(seconds / SIM_STEP);
    while (steps > 0) {
      const batch = Math.min(steps, 30);
      engine.runSteps(batch);
      steps -= batch;
    }
  };
  // Real elapsed time -> fixed steps. Simulated time always advances at the
  // scheduler's rate; if the GPU cannot keep up, frames get longer instead of
  // drying being slowed down.
  engine.advance = (realSeconds) => {
    const steps = stepsForFrame(engine.scheduler, realSeconds);
    engine.runSteps(steps);
    return steps;
  };

  engine.setEnvironment = (patch = {}) => {
    engine.env = { ...engine.env, ...patch };
  };

  engine.dryAll = () => {
    flush();
    writeParams(SIM_STEP);
    const encoder = device.createCommandEncoder();
    const pass = encoder.beginComputePass();
    dispatchGrid(pass, sim.dryAll);
    pass.end();
    device.queue.submit([encoder.finish()]);
  };

  engine.pigmentSlots = () => slots.map((p) => (p ? p.id : null));

  // ---------------------------------------------------------------- surfaces
  // Visual paper relief for the light pass (does not affect physics).
  engine.setDisplaySurface = (heightField, { relief = 1, paperColor = null, sheen = null } = {}) => {
    device.queue.writeTexture({ texture: paperTex }, heightField, { bytesPerRow: width * 4 }, [width, height]);
    display.reliefStrength = relief;
    if (paperColor) display.paperAlbedo = hexToSrgb(paperColor).map(srgbToLinear);
    if (sheen !== null) display.sheen = sheen;
  };
  // Physics surface used by the simulation from now on (existing paint is kept).
  engine.setPhysicsSurface = (next) => {
    const data = new Float32Array(4 * n);
    data.set(next.heightField, 0);
    data.set(next.absorbency, n);
    data.set(next.capillary, 2 * n);
    data.set(next.pinning, 3 * n);
    device.queue.writeBuffer(SURF, 0, data);
    engine.surface = next;
    writeParams(SIM_STEP);
  };

  // ---------------------------------------------------------------- rendering
  let lightPipe = null;
  let maxElevation = 1;
  const writeLight = (light, mode) => {
    const L = lightVector(light);
    const colour = lightColour(light.temperature);
    const data = new ArrayBuffer(LIGHT_UNIFORM_BYTES);
    const fl = new Float32Array(data);
    const u = new Uint32Array(data);
    fl.set([L[0], L[1], L[2], light.intensity, colour[0], colour[1], colour[2], light.ambient, light.specular, light.roughness, light.shadowSoftness, light.depthScale, 0.9 / (light.intensity * L[2] + light.ambient)], 0);
    u[13] = VIEWS[light.view] ?? 0;
    fl[14] = light.maxElevation || maxElevation * 6;
    u[15] = mode;
    fl[16] = width;
    fl[17] = height;
    fl[18] = 0.9 * display.reliefStrength * (light.paperRelief ?? 1);
    fl[19] = display.sheen ?? engine.surface.preset.sheen ?? 0.06;
    fl.set(display.paperAlbedo, 20);
    fl.set(display.substrateAlbedo, 24);
    device.queue.writeBuffer(LIGHT, 0, data);
  };

  // Shades dirty tiles (compute) into the albedo/elevation/material textures.
  const encodeShade = (encoder) => {
    const pass = encoder.beginComputePass();
    pass.setBindGroup(0, shadeGroup);
    pass.setPipeline(shadePipe);
    pass.dispatchWorkgroups(Math.ceil(width / 8), Math.ceil(height / 8));
    pass.setPipeline(clearDirtyPipe);
    pass.dispatchWorkgroups(Math.ceil(nt / 64));
    pass.end();
  };

  // Draws the lit result into a texture view. mode 0 opaque, 1 premultiplied layer, 2 paper only.
  engine.renderToView = (view, format, lightOptions = {}, mode = 0) => {
    flush();
    writeParams(SIM_STEP);
    writeLight({ ...DEFAULT_LIGHT, ...lightOptions }, mode);
    const encoder = device.createCommandEncoder();
    if (mode !== 2) encodeShade(encoder);
    const rp = encoder.beginRenderPass({ colorAttachments: [{ view, loadOp: 'clear', storeOp: 'store', clearValue: { r: 0, g: 0, b: 0, a: 0 } }] });
    rp.setPipeline(pipes.lightPipeline(format));
    rp.setBindGroup(0, lightGroup);
    rp.draw(3);
    rp.end();
    device.queue.submit([encoder.finish()]);
  };

  // Development Lab: render into an on-page canvas.
  engine.attachCanvas = (canvas) => {
    const context = canvas.getContext('webgpu');
    const format = navigator.gpu.getPreferredCanvasFormat();
    context.configure({ device, format, alphaMode: 'opaque' });
    engine.context = context;
    engine.contextFormat = format;
    lightPipe = format;
  };
  engine.render = (lightOptions = {}) => {
    if (!engine.context) {
      flush();
      const encoder = device.createCommandEncoder();
      encodeShade(encoder);
      device.queue.submit([encoder.finish()]);
      return;
    }
    engine.renderToView(engine.context.getCurrentTexture().createView(), lightPipe, lightOptions, 0);
  };

  // ---------------------------------------------------------------- snapshots (undo)
  // Full copies of the physical state on the GPU; restore marks everything
  // active and dirty so it re-simulates and re-renders.
  engine.snapshotBytes = () => (SCALARS.length * n + 3 * PIGMENT_SLOTS * n + n * BAND_COUNT) * 4;
  // Snapshot buffers are pooled: undo history reuses released copies instead
  // of allocating ~50 floats per cell on every stroke.
  const snapshotPool = [];
  engine.captureSnapshot = () => {
    flush();
    const copy = (src, bytes) => {
      const pooled = snapshotPool.findIndex((b) => b.size === bytes);
      if (pooled >= 0) return snapshotPool.splice(pooled, 1)[0];
      return device.createBuffer({ size: bytes, usage: GPUBufferUsage.COPY_SRC | GPUBufferUsage.COPY_DST });
    };
    const parts = [[S, SCALARS.length * n * 4], [P, 3 * PIGMENT_SLOTS * n * 4], [R, n * BAND_COUNT * 4]];
    const encoder = device.createCommandEncoder();
    const copies = parts.map(([src, bytes]) => {
      const dst = copy(src, bytes);
      encoder.copyBufferToBuffer(src, 0, dst, 0, bytes);
      return dst;
    });
    device.queue.submit([encoder.finish()]);
    return { copies, slots: slots.slice(), bytes: engine.snapshotBytes() };
  };
  engine.restoreSnapshot = (snapshot) => {
    if (!snapshot || !snapshot.copies) return false;
    flush();
    const parts = [S, P, R];
    const encoder = device.createCommandEncoder();
    snapshot.copies.forEach((src, k) => encoder.copyBufferToBuffer(src, 0, parts[k], 0, src.size));
    device.queue.submit([encoder.finish()]);
    snapshot.slots.forEach((p, k) => { slots[k] = p; });
    writePigments();
    const tiles = new Uint32Array(5 * nt + GLOBAL.count);
    tiles.fill(1, 0, nt);
    tiles.fill(1, 2 * nt, 3 * nt);
    device.queue.writeBuffer(T, 0, tiles);
    return true;
  };
  engine.destroySnapshot = (snapshot) => {
    if (!snapshot || !snapshot.copies) return;
    // Keep a few sets for reuse; release the rest.
    for (const b of snapshot.copies) {
      if (snapshotPool.length < 6) snapshotPool.push(b);
      else b.destroy();
    }
    snapshot.copies = null;
  };
  // Paper colour change: cells without dried paint take the new substrate;
  // dried paint keeps the paper it dried on (it is baked into its stack).
  let currentSubstrate = Float32Array.from(initialSpectrum);
  engine.substrate = () => currentSubstrate;
  engine.setSubstrate = (spectrum) => {
    const next = Float32Array.from(spectrum);
    if (next.every((v, b) => Math.abs(v - currentSubstrate[b]) < 1e-7)) return;
    flush();
    const data = new ArrayBuffer(SUBSTRATE_UNIFORM_BYTES);
    new Float32Array(data, 0, BAND_COUNT).set(currentSubstrate);
    new Float32Array(data, 48, BAND_COUNT).set(next);
    new Uint32Array(data, 96, 1)[0] = n;
    const uniform = device.createBuffer({ size: SUBSTRATE_UNIFORM_BYTES, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
    device.queue.writeBuffer(uniform, 0, data);
    const pipeline = getSubstratePipeline(device);
    const group = device.createBindGroup({ layout: pipeline.getBindGroupLayout(0), entries: [{ binding: 0, resource: { buffer: R } }, { binding: 1, resource: { buffer: uniform } }] });
    const encoder = device.createCommandEncoder();
    const pass = encoder.beginComputePass();
    pass.setPipeline(pipeline);
    pass.setBindGroup(0, group);
    pass.dispatchWorkgroups(Math.ceil(n / 64));
    pass.end();
    device.queue.submit([encoder.finish()]);
    device.queue.onSubmittedWorkDone().then(() => uniform.destroy());
    currentSubstrate = next;
    display.substrateAlbedo = spectrumAlbedo(next);
    display.paperAlbedo = display.substrateAlbedo;
  };

  // Pre-allocates one snapshot set so the first stroke does not allocate.
  engine.warmSnapshots = () => {
    const sets = [SCALARS.length * n * 4, 3 * PIGMENT_SLOTS * n * 4, n * BAND_COUNT * 4];
    for (const bytes of sets) if (!snapshotPool.some((b) => b.size === bytes)) snapshotPool.push(device.createBuffer({ size: bytes, usage: GPUBufferUsage.COPY_SRC | GPUBufferUsage.COPY_DST }));
  };

  // ---------------------------------------------------------------- readback (validation, saving)
  engine.readback = async () => {
    flush();
    const copy = async (src, bytes) => {
      const staging = device.createBuffer({ size: bytes, usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST });
      const encoder = device.createCommandEncoder();
      encoder.copyBufferToBuffer(src, 0, staging, 0, bytes);
      device.queue.submit([encoder.finish()]);
      await staging.mapAsync(GPUMapMode.READ);
      const out = new Uint8Array(staging.getMappedRange().slice(0));
      staging.unmap();
      staging.destroy();
      return out.buffer;
    };
    const s = new Float32Array(await copy(S, SCALARS.length * n * 4));
    const p = new Float32Array(await copy(P, 3 * PIGMENT_SLOTS * n * 4));
    const r = new Float32Array(await copy(R, n * BAND_COUNT * 4));
    const t = new Uint32Array(await copy(T, (5 * nt + GLOBAL.count) * 4));
    const fields = {};
    SCALARS.forEach((name, f) => { fields[name] = s.subarray(f * n, (f + 1) * n); });
    const layer = (l) => Array.from({ length: PIGMENT_SLOTS }, (_, k) => p.subarray((l * PIGMENT_SLOTS + k) * n, (l * PIGMENT_SLOTS + k + 1) * n));
    return { fields, wetPig: layer(0), depPig: layer(1), bodyPig: layer(2), dryRefl: r, activeTiles: t.subarray(0, nt), globals: t.subarray(4 * nt) };
  };

  // Small readback of the GPU's own step counters (benchmark sanity checks).
  engine.readCounters = async () => {
    const bytes = GLOBAL.count * 4;
    const staging = device.createBuffer({ size: bytes, usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST });
    const encoder = device.createCommandEncoder();
    encoder.copyBufferToBuffer(T, 4 * nt * 4, staging, 0, bytes);
    device.queue.submit([encoder.finish()]);
    await staging.mapAsync(GPUMapMode.READ);
    const g = new Uint32Array(staging.getMappedRange().slice(0));
    staging.unmap();
    staging.destroy();
    return { regionTiles: g[GLOBAL.regionCount], waterSubsteps: g[GLOBAL.nWater], bodySubsteps: g[GLOBAL.nBody] };
  };

  // Writes a CPU paint state (same size) into the GPU buffers (load, undo).
  engine.upload = (state) => {
    const s = new Float32Array(SCALARS.length * n);
    SCALARS.forEach((name, f) => s.set(state[name], f * n));
    device.queue.writeBuffer(S, 0, s);
    const p = new Float32Array(3 * PIGMENT_SLOTS * n);
    for (let k = 0; k < PIGMENT_SLOTS; k += 1) {
      p.set(state.wetPig[k], k * n);
      p.set(state.depPig[k], (PIGMENT_SLOTS + k) * n);
      p.set(state.bodyPig[k], (2 * PIGMENT_SLOTS + k) * n);
      slots[k] = state.slots[k] || null;
    }
    device.queue.writeBuffer(P, 0, p);
    device.queue.writeBuffer(R, 0, state.dryRefl);
    const tiles = new Uint32Array(5 * nt + GLOBAL.count);
    tiles.fill(1, 0, nt);
    tiles.fill(1, 2 * nt, 3 * nt);
    device.queue.writeBuffer(T, 0, tiles);
    writePigments();
  };

  // Benchmark helper: a wash of the given depth over a fraction of the canvas
  // (top-left rectangle), with ultramarine suspended in it; tiles activated.
  engine.debugFlood = (depth = 0.3, fraction = 1) => {
    const k = slotFor(resolvePigment('ultramarine'));
    const ww = Math.max(1, Math.round(width * Math.sqrt(fraction)));
    const wh = Math.max(1, Math.round(height * Math.sqrt(fraction)));
    const water = new Float32Array(n);
    const pigment = new Float32Array(n);
    for (let y = 0; y < wh; y += 1) {
      for (let x = 0; x < ww; x += 1) {
        water[y * width + x] = depth;
        pigment[y * width + x] = depth * 0.4;
      }
    }
    device.queue.writeBuffer(S, SCALARS.indexOf('water') * n * 4, water);
    device.queue.writeBuffer(P, k * n * 4, pigment);
    const tiles = new Uint32Array(nt);
    for (let ty = 0; ty < Math.ceil(wh / TILE_SIZE); ty += 1) for (let tx = 0; tx < Math.ceil(ww / TILE_SIZE); tx += 1) tiles[ty * tilesX + tx] = 1;
    device.queue.writeBuffer(T, 0, tiles);
    writeParams(SIM_STEP);
  };

  engine.destroy = () => {
    buffers.forEach((b) => b.destroy());
    snapshotPool.forEach((b) => b.destroy());
    [albedoTex, elevationTex, materialTex, paperTex].forEach((t) => t.destroy());
  };

  engine.info = {
    tiles: nt,
    maxWaterSubsteps: maxW,
    maxBodySubsteps: MAX_BODY_SUBSTEPS,
    dispatchesPerStep: 3 + maxW * 6 + 1 + 4 + 4 + MAX_BODY_SUBSTEPS * 4 + 3,
  };
  engine.setDisplaySurface(surface.heightField, { relief: 1 });
  writePigments();
  writeParams(SIM_STEP);
  return engine;
}

export { TILE_SIZE };
